import React from 'react';

const MAX_PAGE_BUTTONS = 5;

/**
 * Build the window of page numbers to render around the current page.
 * @param {number} page
 * @param {number} pageCount
 * @returns {number[]}
 */
const buildPageWindow = (page, pageCount) => {
  const size = Math.min(MAX_PAGE_BUTTONS, pageCount);

  return Array.from({ length: size }, (_, index) => {
    if (pageCount <= MAX_PAGE_BUTTONS) return index + 1;
    if (page <= 3) return index + 1;
    if (page >= pageCount - 2) return pageCount - MAX_PAGE_BUTTONS + 1 + index;
    return page - 2 + index;
  });
};

/**
 * Numbered pagination matching the rest of the admin panel.
 */
const Pagination = ({ page, pageCount, onPageChange, disabled = false }) => {
  if (!pageCount || pageCount <= 1) return null;

  const pages = buildPageWindow(page, pageCount);

  return (
    <div className='px-4 md:px-6 py-4 border-t border-gray-200 flex items-center justify-between'>
      <p className='text-sm text-gray-700'>
        Page <span className='font-medium'>{page}</span> of{' '}
        <span className='font-medium'>{pageCount}</span>
      </p>

      <nav
        className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
        aria-label='Pagination'
      >
        <button
          type='button'
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1 || disabled}
          className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
        >
          <span className='sr-only'>Previous</span>
          <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
            <path
              fillRule='evenodd'
              d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
              clipRule='evenodd'
            />
          </svg>
        </button>

        {pages.map(pageNum => (
          <button
            key={pageNum}
            type='button'
            onClick={() => onPageChange(pageNum)}
            disabled={disabled}
            aria-current={pageNum === page ? 'page' : undefined}
            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
              pageNum === page
                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {pageNum}
          </button>
        ))}

        <button
          type='button'
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount || disabled}
          className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
        >
          <span className='sr-only'>Next</span>
          <svg className='h-5 w-5' fill='currentColor' viewBox='0 0 20 20'>
            <path
              fillRule='evenodd'
              d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
              clipRule='evenodd'
            />
          </svg>
        </button>
      </nav>
    </div>
  );
};

export default Pagination;
